import {
  AGENT_GHOST,
  AGENT_PRIMARY,
  AGENT_SECONDARY,
  DESTRUCTIVE,
  GHOST,
  LINK,
  NEGATIVE,
  PRIMARY,
  PROMOTIONAL,
  SECONDARY,
  TERTIARY,
} from '../../../../utils/spds/constants/buttons';
import {
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  LINK_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import AgentGhostButton from './agent/ghost.gjs';
import AgentPrimaryButton from './agent/primary.gjs';
import AgentSecondaryButton from './agent/secondary.gjs';
import DestructiveButton from './destructive.gjs';
import GhostButton from './ghost.gjs';
import LinkButton from './link.gjs';
import NegativeButton from './negative.gjs';
import PrimaryButton from './primary.gjs';
import PromotionalButton from './promotional.gjs';
import SecondaryButton from './secondary.gjs';
import Super from '../../core/base.gjs';
import TertiaryButton from './tertiary.gjs';

const BUTTON_COMPONENTS = {
  [AGENT_GHOST]: AgentGhostButton,
  [AGENT_PRIMARY]: AgentPrimaryButton,
  [AGENT_SECONDARY]: AgentSecondaryButton,
  [DESTRUCTIVE]: DestructiveButton,
  [GHOST]: GhostButton,
  [LINK]: LinkButton,
  [NEGATIVE]: NegativeButton,
  [PRIMARY]: PrimaryButton,
  [PROMOTIONAL]: PromotionalButton,
  [SECONDARY]: SecondaryButton,
  [TERTIARY]: TertiaryButton,
};

const METADATA = Metadata.define({
  description:
    'Button that can be set to any simple SPDS button type through the `@buttonType` argument.\n\n' +
    '### Routing\n\n' +
    'Every type this component can render extends `Spds::Core::Button`, so the routing arguments ' +
    'are passed through to the delegate and behave exactly as they do on a concrete variant: ' +
    'supplying `@route` renders an anchor with a generated `href`.',
  extends: [
    pick(BUTTON_ARGS, ['buttonType', 'pill']),
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled', 'loading']),
    pick(CONTAINER_ARGS, ['fitContent', 'fitContentHeight', 'fitContentWidth', 'inline']),
    pick(CONTENT_ARGS, [{ name: 'text', initialValue: 'Button' }]),
    LINK_ARGS,
  ],
  name: 'Variable Button',
});

export default class SpdsActionButtonVariable extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  get buttonType() {
    return this.argOrDefault('buttonType');
  }

  get button() {
    return BUTTON_COMPONENTS[this.buttonType] ?? PrimaryButton;
  }

  /**
   * Collapses `@model`/`@models` into the single array form the delegate receives.
   *
   * Forwarding both arguments verbatim is not an option. A pass-through such as
   * `@model={{@model}}` puts `model` in the delegate's args whether or not the caller supplied
   * it, and the delegate keys its incomplete-model detection on argument *presence* — so every
   * model-less link would be reported as incomplete. Collapsing to one argument keeps that
   * detection accurate: an absent model becomes `[]`, which reads as complete, while a nullish
   * one is preserved so the delegate still degrades and warns.
   * @return {Array|undefined} dynamic segment values, or undefined when @models is nullish
   */
  get routeModels() {
    let { model, models } = this.args;

    if ('models' in this.args) return models;
    if ('model' in this.args) return [model];

    return [];
  }

  <template>
    <this.button
      @activeClass={{@activeClass}}
      @activeWhen={{@activeWhen}}
      @autofocus={{@autofocus}}
      @disabled={{@disabled}}
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @id={{this.id}}
      @inline={{@inline}}
      @loading={{@loading}}
      @models={{this.routeModels}}
      @pill={{@pill}}
      @query={{@query}}
      @replace={{@replace}}
      @route={{@route}}
      @size={{@size}}
      @text={{@text}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield}}
    </this.button>
  </template>
}

export const ACTION_BUTTON_VARIABLE_METADATA = SpdsActionButtonVariable.metadata;
